// Interview invite — pure helpers: map the GET response to the shape the page
// renders, derive the display phase, and format times in the candidate's local
// timezone (product decision — the API sends times in UTC).

const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export function titleCase(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Backend import-job titles sometimes carry an internal `[IMPORTED]` marker —
// strip it so candidates never see data-pipeline plumbing.
const cleanJobTitle = (title) =>
  String(title || '').replace(/^\s*\[imported\]\s*/i, '');

export function firstNameOf(name) {
  const first =
    String(name || '')
      .trim()
      .split(/\s+/)[0] || '';
  return first ? titleCase(first) : 'there';
}

const toDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Map the raw `data` payload to the normalized invite the component consumes.
// Tolerant of two reschedule shapes: the magic-link flow's flat
// `previous_start_time`/`rescheduled_at`/`reschedule_reason`, and the
// protected (My Applications) flow's nested `previous_interview` object —
// prefers the flat fields when both happen to be present.
export function mapInvite(data) {
  if (!data) return null;
  const interviewers = Array.isArray(data.interviewers)
    ? data.interviewers.filter(Boolean)
    : [data.interviewer].filter(Boolean);
  const prev = data.previous_interview || null;
  return {
    fullName: titleCase(data.candidate_name),
    firstName: firstNameOf(data.candidate_name),
    jobTitle: cleanJobTitle(data.job_title),
    organization: data.organization || '',
    round: data.round || '',
    interviewers,
    format: data.format || '',
    start: toDate(data.start_time),
    end: toDate(data.end_time),
    timeZone: data.time_zone || '',
    status: norm(data.status),
    isCancelled: !!data.is_cancelled,
    joinAvailable: !!data.join_available,
    joinUrl: data.join_url || '',
    unlockMins: Number(data.unlock_minutes_before) || 0,
    graceMins: Number(data.grace_minutes_after) || 0,
    supportEmail: data.support_email || '',
    reference: data.reference || '',
    ack: {
      required: !!data.requires_acknowledgment,
      // API misspells this key (`candidate_acknowedged`, no `l`) — read both.
      acknowledged: !!(
        data.candidate_acknowedged ?? data.candidate_acknowledged
      ),
      rejected: !!data.candidate_rejected,
    },
    // Reschedule extras — present only when status === 'rescheduled'.
    previousStart: toDate(data.previous_start_time ?? prev?.start_time),
    rescheduledAt: toDate(data.rescheduled_at ?? prev?.cancelled_at),
    rescheduleReason: data.reschedule_reason || '',
  };
}

// Derive which state to render. Status-driven terminals first, then the time
// gate. The join button unlocks only when the SERVER says so (`joinAvailable`
// + `joinUrl`) — the client never fabricates the link.
export function derivePhase(inv, now) {
  if (!inv || !inv.start || !inv.end) return 'invalid';
  if (inv.ack.rejected) return 'declined';
  if (inv.isCancelled) return 'cancelled';
  switch (inv.status) {
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    case 'no show':
      return 'noShow';
    case 'completed':
      return 'done';
    default:
      break;
  }
  const closeAt = inv.end.getTime() + inv.graceMins * 60000;
  if (now >= closeAt) return 'done';
  if (inv.joinAvailable && inv.joinUrl) return 'live';
  const unlockAt = inv.start.getTime() - inv.unlockMins * 60000;
  if (now < unlockAt) return 'locked';
  return 'opening'; // inside the window, server hasn't opened the link yet
}

export const isScheduledPhase = (phase) =>
  phase === 'locked' || phase === 'opening' || phase === 'live';

// Headline shown after "Hi {name}, ".
export function headlineFor(phase, status) {
  if (status === 'rescheduled' && isScheduledPhase(phase)) {
    return 'your interview has moved.';
  }
  switch (phase) {
    case 'done':
      return 'your interview has ended.';
    case 'cancelled':
      return 'this interview was cancelled.';
    case 'noShow':
      return 'this interview was missed.';
    case 'declined':
      return "you've let us know you can't attend.";
    default:
      return 'your interview is confirmed.';
  }
}

// Format start/end in the candidate's LOCAL timezone.
export function formatWhen(start, end) {
  const dateText = new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(start);
  const t = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const tzLabel =
    new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(start)
      .find((p) => p.type === 'timeZoneName')?.value || '';
  const timeText = `${t.format(start)} – ${t.format(end)}${
    tzLabel ? ` ${tzLabel}` : ''
  }`;
  const durationText = `${Math.round((end - start) / 60000)} minutes`;
  return { dateText, timeText, durationText };
}

// Short "Sat 11 Sept, 3:30 PM" — used for the previous (rescheduled) time.
export function formatShort(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

// "8 September" — used for the "Rescheduled on" banner.
export function formatDay(date) {
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
  }).format(date);
}

export function until(ms) {
  const m = Math.ceil(ms / 60000);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  if (h < 24) return rm ? `${h}h ${rm}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}
